// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as BABYLON from 'babylonjs';
import * as Interfaces from '../../Interfaces/Interfaces';
import * as Enumerations from '../../Enumerations/Enumerations';
import * as Data from '../Data/WebData';
import MeshFactory from '../workers/MeshFactory';
import MeshManager from './MeshManager';
class AnnotationManager implements Interfaces.Interfaces.IAnnotationManager {

    public static SIN_75 = 0.9659258263;
    public static COS_75 = 0.2588190451;
    public static SIN_72 = 0.9510565163;
    public static COS_72 = 0.3090169944;
    public static SIN_60 = 0.8660254038;
    public static COS_60 = 0.5;
    public static TAN_60 = 1.732050808;
    public static SIN_45 = 0.7071067812;
    public static COS_45 = 0.7071067812;
    public static TAN_45 = 1;
    public static SIN_36 = 0.5877852523;
    public static COS_36 = 0.8090469944;
    public static TAN_36 = 0.726542528;
    public static COS_30 = 0.8660254038;
    public static SIN_30 = 0.5;
    public static SIN_15 = 0.2588190451;
    public static COS_15 = 0.9659258263;

    public static TRIANGLE_MARGIN = 2;
    public static DIAMOND_MARGIN = 2;
    public static PENTAGON_MARGIN = 2;
    public static HEXAGON_MARGIN = 5;
    public static ELLIPSE_MARGIN = 5;
    public static CIRCLE_MARGIN = 2;
    public static BANNER_MARGIN = 5;
    public static RECTANGLE_MARGIN = 5;
    public static MARKED_POINT_RADIUS = 15;
    public static MEASUREMENT_POINT_RADIUS = 37.5;

    public markedPoints: { [n: string]: Interfaces.Interfaces.I3DAnnotation } = {};
    public labels: Interfaces.Interfaces.I2DAnnotation[] = [];
    public measurements: Interfaces.Interfaces.I3DAnnotation[] = [];
    
    private _pendingSelectionMPs: { [mpId: string]: boolean; } = {};
    private _hiddenIds: { [n: string] : boolean } = {};
    private _hiddenMarkedPoints: Interfaces.Interfaces.I3DAnnotation[] = [];
    private _hiddenLabels: Interfaces.Interfaces.I2DAnnotation[] = [];
    private _renderCanvas: CanvasRenderingContext2D;
    private _materialManager: Interfaces.Interfaces.IMaterialsManager;
    private _meshFactory: MeshFactory;
    private _meshManager: MeshManager;
    private _selectionHandler: Interfaces.Interfaces.IEventManager;

    private _tripodInstances: { [n: string]: BABYLON.InstancedMesh } = {};
    private _pickedTripods: { [n: string]: BABYLON.InstancedMesh } = {};

    private _scanPositions: { [n: string]: Data.GraphicData.Vector3D } = {};

    private static MEASUREMENT_TAG_DISTANCE = 1;

    constructor(renderCanvas: CanvasRenderingContext2D, materialManager: Interfaces.Interfaces.IMaterialsManager, meshManager: MeshManager, meshFactory: MeshFactory, eventHandler: Interfaces.Interfaces.IEventManager ) {
        this._renderCanvas = renderCanvas;
        this._materialManager = materialManager;
        this._meshFactory = meshFactory;
        this._meshManager = meshManager;
        this._selectionHandler = eventHandler;
    }

    processMarkedPointData(markedPointData: any, blockId : string, pageId : string) {

        this.addMarkedPoint(markedPointData.id, markedPointData.markedPointMaterialId, new Data.GraphicData.MarkedPoint(markedPointData.markedPointInfo.data), blockId, pageId, markedPointData.assetMaterialOverride);
    }

    processLabelData(labelData: Interfaces.Interfaces.ILabelDataObject) {
        this.addLabel(labelData.ID, new Data.GraphicData.Label(labelData.Data));
    }

    addMeasurement(markerPosition: BABYLON.Vector3, surfaceNormal: BABYLON.Vector3, assetId: string, assetType: Enumerations.PickObjectType) {

        var id = (this.measurements.length + 1).toString();
        var measurementMaterial = this._materialManager.getMeasurementMaterial();
        var primitiveMaterial = this._materialManager.getMaterial(measurementMaterial.markerAppeararnceId);

        var measurement = new MeasurementAnnotation(id, assetId, markerPosition, markerPosition.add(surfaceNormal.scale(AnnotationManager.MEASUREMENT_TAG_DISTANCE)), this._renderCanvas, measurementMaterial);
        this.measurements.push(measurement);

        var args: Interfaces.Interfaces.IMeasurementCreatedEvent = {
            id: id,
            markerPosition: JSON.stringify(markerPosition),
            assetId: assetId,
            assetType: assetType
        };

        var sphereInfo = new Data.GraphicData.Sphere(JSON.parse("{\"Pos\":{\"X\":" + markerPosition.x * 1000 + ", \"Y\":" + markerPosition.y * 1000 + ",\"Z\":" + markerPosition.z * 1000 + "},\"Ra\": " + AnnotationManager.MEASUREMENT_POINT_RADIUS + "}"));
        var primitive = this._meshFactory.createSphere(id, "MeasurementMaterial", sphereInfo);

        primitive.material = primitiveMaterial;

        var meshes: BABYLON.Mesh[] = [];
        meshes.push(primitive);

        var minExtents = new Data.GraphicData.Vector3D();
        minExtents.X = markerPosition.x * 1000  - 100;
        minExtents.Y = markerPosition.y * 1000 - 100;
        minExtents.Z = markerPosition.z * 1000 - 100;

        var maxExtents = new Data.GraphicData.Vector3D();
        maxExtents.X = markerPosition.x * 1000 + 100;
        maxExtents.Y = markerPosition.y * 1000 + 100;
        maxExtents.Z = markerPosition.z * 1000 + 100;
        primitive.isPickable = null;

        this._meshManager.addGraphicObject(id, minExtents, maxExtents, measurementMaterial.markerAppeararnceId, meshes);

        // This is done here for now so that the primitives for the marked points are merged. This can be changed in future if the primitives are created on the workers.
        this._meshManager.mergeMeshes(true);

        this._selectionHandler.fireEvent(Enumerations.EventType.MEASUREMENTCREATED, args);
    }

    addLabel(id: string, labelInfo: Data.GraphicData.Label): void {

        var label = new LabelAnnotation(id, this._renderCanvas, labelInfo);

        if (this._hiddenIds[id] == true) {
            this._hiddenLabels.push(label);
        }
        else {
            this.labels.push(label);
        }
    }
    addMarkedPoint(id: string, materialId: string, markedPointInfo: Data.GraphicData.MarkedPoint, blockId: string, pageId: string, assetMaterialOverride?: string): void {

        if (this.markedPoints[id] == null) {
            
            var mpMaterial = this._materialManager.getMarkedPointMaterial(materialId, assetMaterialOverride);

            if (mpMaterial.tagVisible) {
                var mp = new MarkedPointAnnotation(id, this._renderCanvas, markedPointInfo, mpMaterial);

                if (this._hiddenIds[id] == true) {
                    this._hiddenMarkedPoints.push(mp);
                }
                else {
                    this.markedPoints[id] = mp;
                }

                var spherePosition = markedPointInfo.Position;
            }
        }

        if(this._pendingSelectionMPs[id] == true)
        {
            this.pickMarkedPoint(id, true, false, false);
            delete this._pendingSelectionMPs[id];
        }
    }

    addScans(ids: string[], positions: Data.GraphicData.Vector3D[]): void {

        for (var x = 0; x < ids.length; x++) {
            this._scanPositions[ids[x]] = positions[x];
        }        
    }

    containsScan(id: string): boolean {
        if (this._tripodInstances[id] != null) {
            return true;
        }

        return false;
    }
    createScans(): void {

        var tripods = this._meshFactory.createTripodInstances(this._scanPositions, this._materialManager.getScanMaterial());

        for (var i = 0; i < tripods.length; i++) {
            this._tripodInstances[tripods[i].id] = tripods[i];
        }
    }

    getScanPosition(id: string)
    {
        return this._scanPositions[id];
    }

    displayScans(show: boolean) {

        if (this._tripodInstances != null) {
            const keys = Object.keys(this._tripodInstances);
            for (let x = 0; x < keys.length; x++) {
                this._tripodInstances[keys[x]].setEnabled(show);
            }

            if (this._pickedTripods != null) {
                const pickedKeys = Object.keys(this._pickedTripods);
                for (let p = 0; p < pickedKeys.length; p++) {
                    this._pickedTripods[pickedKeys[p]].setEnabled(show);
                }
            }
        }
    }

    clearAnnotations() {

        this.labels = [];
        this.markedPoints = {};
        this._hiddenMarkedPoints = [];
        this._hiddenIds = {};
    }
    clearMeasurements() {

        for (var i = 0; i < this.measurements.length; i++) {

            var m = this.measurements[i];

            var masterMeshIds = this._meshManager.getMasterKeysOfObject(m.id);

            for (var z = 0; z < masterMeshIds.length; z++) {
                this._meshManager.clearMesh(masterMeshIds[z]);
            }
        }

        this.measurements = [];

        this._selectionHandler.fireEvent(Enumerations.EventType.MEASUREMENTSCLEARED);
    }

    isMarkedPointSelected(markedPointId: string): boolean {

        var keys = Object.keys(this.markedPoints);

        for (var i = 0; i < keys.length; i++) {

            if (this.markedPoints[keys[i]] && this.markedPoints[keys[i]].id == markedPointId) {

                return this.markedPoints[keys[i]].selected;
            }
        }
    }

    pickScan(scanId: string, isSelected :boolean): boolean {

        var keys = Object.keys(this._tripodInstances);

        var scanPosition = this.getScanPosition(scanId);
        for (var i = 0; i < keys.length; i++) {
            if (this._tripodInstances[keys[i]] && this._tripodInstances[keys[i]].id == scanId) {
                if (isSelected) {
                    var isEnabled = this._tripodInstances[keys[i]].isEnabled();

                    if (this._pickedTripods[keys[i]] == null) {
                        var selMat = this._materialManager.getMaterial(this._materialManager.getSelectionMaterialName(this._tripodInstances[keys[i]].material.id));
                        var tripod = this._meshFactory.createTripodInstance(keys[i], scanPosition, selMat);
                        this._pickedTripods[keys[i]] = tripod;
                    }

                    this._tripodInstances[keys[i]].setEnabled(false);
                    this._pickedTripods[keys[i]].setEnabled(isEnabled);
                }
                else {
                    var isEnabled = this._pickedTripods[keys[i]].isEnabled();

                    if (this._pickedTripods[keys[i]]) {
                        this._pickedTripods[keys[i]].dispose();
                        delete this._pickedTripods[keys[i]];
                    }

                    this._tripodInstances[keys[i]].setEnabled(isEnabled);
                }
            }
        }
       
        return false;
    }

    pickMarkedPoint(markedPointId: string, isSelected: boolean, fireEvents: boolean, rememberPendingSelection : boolean): boolean {

        var keys = Object.keys(this.markedPoints);
        
        for (var i = 0; i < keys.length; i++) {

            if (this.markedPoints[keys[i]] && this.markedPoints[keys[i]].id == markedPointId) {


                this.markedPoints[keys[i]].selected = isSelected;

                if (fireEvents) {
                    this.selectionChanged([markedPointId], isSelected);
                }
                return true;
            }
        }

        if(rememberPendingSelection)
        {
            this._pendingSelectionMPs[markedPointId] = true;
        }
        
        return false;
    }

    selectionChanged(selectedObjectIds: string[], isSelected: boolean) {

        var args: Interfaces.Interfaces.ISelectionChangedEvent = { isSelected: isSelected, objectIds: selectedObjectIds, allSelectedObjects: [], objectSelectionType: Enumerations.PickObjectType.Object };
        this._selectionHandler.fireEvent(Enumerations.EventType.SELECTIONCHANGED, args);
    }

    pickMarkedPointByClick(positionX: number, positionY : number) : string {

        var pickedMarkedPointId = "";
        var newlyPickedMPIndex = -1;
        
        var keys = Object.keys(this.markedPoints);

        var keys = Object.keys(this.markedPoints);
        
        for (var i = 0; i < keys.length; i++) {
            if (this.markedPoints[keys[i]] && this.markedPoints[keys[i]].pick(positionX, positionY)) {
                newlyPickedMPIndex = i;
                pickedMarkedPointId = this.markedPoints[keys[i]].id;
                break;
            }
        }

        for (var i = 0; i < keys.length; i++) {
            if (this.markedPoints[keys[i]]) {
                if (i != newlyPickedMPIndex)
                    this.markedPoints[keys[i]].selected = false;
                else {
                    this.markedPoints[keys[i]].selected = !this.markedPoints[keys[i]].selected;
                }
            }
        }

        return pickedMarkedPointId;
    }
    unPickAllMarkedPoints() {

        var keys = Object.keys(this.markedPoints);

        for (var i = 0; i < keys.length; i++) {
            if (this.markedPoints[keys[i]]) {
                this.markedPoints[keys[i]].selected = false;
            }
        }

    }

    public displayAnnotation(ids: string[], show: boolean)
    {
        if (show) {

            for (var i = 0; i < ids.length; i++) {
                this._hiddenIds[ids[i]] = false;
            }

            for (var i = this._hiddenMarkedPoints.length - 1; i > -1; i--) {
                if (ids.indexOf(this._hiddenMarkedPoints[i].id) > -1) {
                    var markedPoint = this._hiddenMarkedPoints[i];
                    this.markedPoints[this._hiddenMarkedPoints[i].id] = (markedPoint);
                    this._hiddenMarkedPoints.splice(i, 1);
                }
            }

            for (var i = this._hiddenLabels.length - 1; i > -1; i--) {
                if (ids.indexOf(this._hiddenLabels[i].labelId) > -1) {
                    var label = this._hiddenLabels[i];
                    this.labels.push(label);
                    this._hiddenLabels.splice(i, 1);
                }
            }
        } else {

            var keys = Object.keys(this.markedPoints);

            for (var i = 0; i < ids.length; i++) {
                this._hiddenIds[ids[i]] = true;
            }

            for (var i = keys.length - 1; i > -1; i--) {
                if (this.markedPoints[keys[i]] != null && ids.indexOf(this.markedPoints[keys[i]].id) > -1) {
                    var markedPoint = this.markedPoints[keys[i]];
                    this._hiddenMarkedPoints.push(markedPoint);
                    this.markedPoints[this.markedPoints[keys[i]].id] = null;
                }
            }

            for (var i = this.labels.length - 1; i > -1; i--) {
                if (ids.indexOf(this.labels[i].labelId) > -1) {
                    var label = this.labels[i];
                    this._hiddenLabels.push(label);
                    this.labels.splice(i, 1);
                }
            }
        }
    }

    public dispose() {
        this.markedPoints = {};
        this.labels = [];
        this.measurements = [];
        this._hiddenLabels = [];
        this._hiddenMarkedPoints = [];
        this._renderCanvas = null;
        this._materialManager = null;
        this._meshFactory = null;
        this._meshManager = null;
        this._selectionHandler = null;
        this._pendingSelectionMPs = {};

        this._tripodInstances = {};
        this._pickedTripods = {};
    }
}

class MeasurementAnnotation implements Interfaces.Interfaces.I3DAnnotation {

    public id: string;
    public assetId: string;
    public selected: boolean = false;

    private static MARGIN = 5;
    private _labelShape: Enumerations.AnnotationLabelShape;
    private _startPosition: BABYLON.Vector3;
    private _endPosition: BABYLON.Vector3;
    private _boundingBox: BABYLON.BoundingBox;
    private _position: BABYLON.Vector2;
    private _textColor: string;
    private _backgroundColor: string;
    private _lineColor: string;
    private _lineWidth: number;
    private _fontHeight: number = 0;
    private _renderCanvas: CanvasRenderingContext2D;
    private _text: string[];
    private _font: string = "";
    private _textWidth: number = 0;
    private _textHeight: number = 0;
    private _shapeVerteces: BABYLON.Vector2[] = [];
    private _shapeFillVerteces: BABYLON.Vector2[] = [];

    constructor(measurementNumber: string, assetId : string, startPosition: BABYLON.Vector3, endPosition: BABYLON.Vector3, renderCanvas: CanvasRenderingContext2D, material: Interfaces.MaterialInterfaces.IAnnotationMaterial) {
        this._renderCanvas = renderCanvas;
        this._startPosition = startPosition;
        this._endPosition = endPosition;
        this._text = [];
        this.id = measurementNumber;
        this.assetId = assetId;
     
        this._text.push(measurementNumber);

        this._boundingBox = new BABYLON.BoundingBox(this._startPosition, this._startPosition);

        this._labelShape = material.tagShape;
        this._textColor = material.captionTextColor;
        this._backgroundColor = material.backgroundColor;
        this._lineColor = material.lineColor;
        this._fontHeight = material.captionFont.Height;
        this._lineWidth = material.lineWidth;
        this._font = (material.captionFont.Bold ? "bold " : "") + (material.captionFont.Italic ? "italic " : "") + (material.captionFont.Strikeout ? "strikeout " : "") + (material.captionFont.Underline ? "underline " : "") + this._fontHeight + "pt " + material.captionFont.FaceName;

        this._renderCanvas.font = this._font;

        for (var i = 0; i < this._text.length; i++) {
            this._textWidth = Math.max(this._textWidth, this._renderCanvas.measureText(this._text[i]).width);
            this._textHeight = material.captionFont.Height * (i + 1) + this.getMarginSize() * i;
        }
    }

    public render(transformation: BABYLON.Matrix, viewport: BABYLON.Viewport): void {

        var viewPlanes = BABYLON.Frustum.GetPlanes(transformation);

        if (!this._boundingBox.isInFrustum(viewPlanes))
            return;

        var mpScreenPosition = this.CalculateScreenPosition(transformation, viewport);
        var mpPrimScreenPosition = this.CalculatePrimitiveScreenPosition(transformation, viewport);

        this.generateLabelShape(mpScreenPosition);

        this._renderCanvas.strokeStyle = this._lineColor;
        this._renderCanvas.fillStyle = this._lineColor;

        this.renderLeaderLine(mpPrimScreenPosition);

        // Draw shape
        this._renderCanvas.beginPath();

        var prevStrokeStyle = this._renderCanvas.strokeStyle;
        
        // Draw Border and Background
        this._renderCanvas.moveTo(this._shapeVerteces[0].x, this._shapeVerteces[0].y);

        for (var i = 1; i < this._shapeVerteces.length; i++) {
            this._renderCanvas.lineTo(this._shapeVerteces[i].x, this._shapeVerteces[i].y);
        }

        this._renderCanvas.fillStyle = this._backgroundColor;
        this._renderCanvas.lineWidth = this._lineWidth;
        this._renderCanvas.fill();
        this._renderCanvas.stroke();

        this._renderCanvas.strokeStyle = this._lineColor;
        this._renderCanvas.fillStyle = this._lineColor;
        
        this._renderCanvas.beginPath();
        this._renderCanvas.moveTo(this._shapeFillVerteces[0].x, this._shapeFillVerteces[0].y);

        for (var i = 1; i < this._shapeFillVerteces.length; i++) {
            this._renderCanvas.lineTo(this._shapeFillVerteces[i].x, this._shapeFillVerteces[i].y);
        }

        this._renderCanvas.fill();

        // Draw Text
        this.renderText(mpScreenPosition);
    }

    public pick(positionX: number, positionY: number): boolean {

        throw new Error("Pick is not supported on measurements");
    }
    public get shapeType(): Enumerations.AnnotationLabelShape {
        return this._labelShape;
    }
    private renderText(mpScreenPosition: BABYLON.Vector3): void {

        var textYPos = mpScreenPosition.y + ((this._fontHeight)) / 2 - (this._textHeight - this._fontHeight) / 2;
        this._renderCanvas.font = this._font;
        this._renderCanvas.strokeStyle = this._textColor;
        this._renderCanvas.fillStyle = this._textColor;

        for (var i = 0; i < this._text.length; i++) {

            this._renderCanvas.fillText(this._text[i], mpScreenPosition.x, textYPos);
            textYPos += this._fontHeight + this.getMarginSize();
        }
    }
    private CalculatePrimitiveScreenPosition(transformation: BABYLON.Matrix, viewport: BABYLON.Viewport): BABYLON.Vector2 {

        var screePosition = BABYLON.Vector3.Project(this._startPosition,
            BABYLON.Matrix.Identity(),
            transformation,
            viewport);

        return new BABYLON.Vector2(screePosition.x, screePosition.y);
    }

    private CalculateScreenPosition(transformation: BABYLON.Matrix, viewport: BABYLON.Viewport): BABYLON.Vector3 {
        var screenPos = BABYLON.Vector3.Project(this._endPosition,
            BABYLON.Matrix.Identity(),
            transformation,
            viewport);

        return screenPos;
    }
    private renderLeaderLine(primitiveScreenPosition: BABYLON.Vector2): void {

        var currentDistance = 0.0;
        var smallestDistance = 0.0;
        var closestPointIndex = 0;

        for (var i = 0; i < this._shapeVerteces.length; i++) {
            var currentDistance = BABYLON.Vector2.Distance(this._shapeVerteces[i], primitiveScreenPosition);

            if (i == 0 || currentDistance < smallestDistance) {
                smallestDistance = currentDistance;
                closestPointIndex = i;
            }
        }

        this._renderCanvas.beginPath();
        this._renderCanvas.moveTo(primitiveScreenPosition.x, primitiveScreenPosition.y);
        this._renderCanvas.lineTo(this._shapeVerteces[closestPointIndex].x, this._shapeVerteces[closestPointIndex].y);
        this._renderCanvas.stroke();
    }
    private generateLabelShape(screenPos: BABYLON.Vector3): void {

        var screenPosition = screenPos.clone();
        this._shapeVerteces = [];
        this._shapeFillVerteces = [];

        this._renderCanvas.font = this._font;

        var width = this._textWidth;
        var height = this._textHeight;

        switch (this.shapeType) {
            case Enumerations.AnnotationLabelShape.RECTANGLE:

                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += width / 2;

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));

                break;
            case Enumerations.AnnotationLabelShape.TRIANGLE:

                var triangleSize = AnnotationManager.TAN_60 * ((width + this.getMarginSize()) / 2.0) + ((height + this.getMarginSize()) / 2.0);

                screenPosition.x += width / 2;

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - triangleSize));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + AnnotationManager.SIN_60 * triangleSize, screenPosition.y + AnnotationManager.COS_60 * triangleSize));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - AnnotationManager.SIN_60 * triangleSize, screenPosition.y + AnnotationManager.COS_60 * triangleSize));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - triangleSize));

                break;
            case Enumerations.AnnotationLabelShape.DIAMOND:

                var diamondSize = ((height + this.getMarginSize()) / 2.0) + ((width + this.getMarginSize()) / 2.0);

                screenPosition.x += width / 2;

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y + diamondSize));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + diamondSize, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - diamondSize));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - diamondSize, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y + diamondSize));

                break;
            case Enumerations.AnnotationLabelShape.PENTAGON:
                var pentagonSize = (Math.max(width + this.getMarginSize(), height + this.getMarginSize()) / 2.0) / AnnotationManager.TAN_36;

                screenPosition.x += width / 2;

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - pentagonSize));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (pentagonSize * AnnotationManager.SIN_72), screenPosition.y - (pentagonSize * AnnotationManager.COS_72)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (pentagonSize * AnnotationManager.SIN_36), screenPosition.y + (pentagonSize * AnnotationManager.COS_36)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (pentagonSize * AnnotationManager.SIN_36), screenPosition.y + (pentagonSize * AnnotationManager.COS_36)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (pentagonSize * AnnotationManager.SIN_72), screenPosition.y - (pentagonSize * AnnotationManager.COS_72)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - pentagonSize));

                break;
            case Enumerations.AnnotationLabelShape.HEXAGON:
                var halfWidth = width / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += width / 2;

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));

                break;
            case Enumerations.AnnotationLabelShape.ELLIPSE:

                var halfWidth = width / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += width / 2;

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_30 * halfHeight), screenPosition.y - (AnnotationManager.COS_30 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_45 * halfHeight), screenPosition.y - (AnnotationManager.COS_45 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_60 * halfHeight), screenPosition.y - (AnnotationManager.COS_60 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_60 * halfHeight), screenPosition.y + (AnnotationManager.COS_60 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_45 * halfHeight), screenPosition.y + (AnnotationManager.COS_45 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_30 * halfHeight), screenPosition.y + (AnnotationManager.COS_30 * halfHeight)));

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_30 * halfHeight), screenPosition.y + (AnnotationManager.COS_30 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_45 * halfHeight), screenPosition.y + (AnnotationManager.COS_45 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_60 * halfHeight), screenPosition.y + (AnnotationManager.COS_60 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_60 * halfHeight), screenPosition.y - (AnnotationManager.COS_60 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_45 * halfHeight), screenPosition.y - (AnnotationManager.COS_45 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_30 * halfHeight), screenPosition.y - (AnnotationManager.COS_30 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));

                break;
            case Enumerations.AnnotationLabelShape.CIRCLE:
                var circleRadius = Math.sqrt(Math.pow((height / 2.0), 2) + Math.pow((width / 2.0), 2)) + this.getMarginSize();

                screenPosition.x += width / 2;

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y + circleRadius));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_15 * circleRadius), screenPosition.y + (AnnotationManager.COS_15 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_30 * circleRadius), screenPosition.y + (AnnotationManager.COS_30 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_45 * circleRadius), screenPosition.y + (AnnotationManager.COS_45 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_60 * circleRadius), screenPosition.y + (AnnotationManager.COS_60 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_75 * circleRadius), screenPosition.y + (AnnotationManager.COS_75 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + circleRadius, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_75 * circleRadius), screenPosition.y - (AnnotationManager.COS_75 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_60 * circleRadius), screenPosition.y - (AnnotationManager.COS_60 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_45 * circleRadius), screenPosition.y - (AnnotationManager.COS_45 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_30 * circleRadius), screenPosition.y - (AnnotationManager.COS_30 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_15 * circleRadius), screenPosition.y - (AnnotationManager.COS_15 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - circleRadius));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_15 * circleRadius), screenPosition.y - (AnnotationManager.COS_15 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_30 * circleRadius), screenPosition.y - (AnnotationManager.COS_30 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_45 * circleRadius), screenPosition.y - (AnnotationManager.COS_45 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_60 * circleRadius), screenPosition.y - (AnnotationManager.COS_60 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_75 * circleRadius), screenPosition.y - (AnnotationManager.COS_75 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - circleRadius, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_75 * circleRadius), screenPosition.y + (AnnotationManager.COS_75 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_60 * circleRadius), screenPosition.y + (AnnotationManager.COS_60 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_45 * circleRadius), screenPosition.y + (AnnotationManager.COS_45 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_30 * circleRadius), screenPosition.y + (AnnotationManager.COS_30 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_15 * circleRadius), screenPosition.y + (AnnotationManager.COS_15 * circleRadius)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y + circleRadius));


                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - circleRadius));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_15 * circleRadius), screenPosition.y - (AnnotationManager.COS_15 * circleRadius)));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_30 * circleRadius), screenPosition.y - (AnnotationManager.COS_30 * circleRadius)));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_45 * circleRadius), screenPosition.y - (AnnotationManager.COS_45 * circleRadius)));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_60 * circleRadius), screenPosition.y - (AnnotationManager.COS_60 * circleRadius)));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_75 * circleRadius), screenPosition.y - (AnnotationManager.COS_75 * circleRadius)));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x - circleRadius, screenPosition.y));

                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x + circleRadius, screenPosition.y));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_75 * circleRadius), screenPosition.y + (AnnotationManager.COS_75 * circleRadius)));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_60 * circleRadius), screenPosition.y + (AnnotationManager.COS_60 * circleRadius)));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_45 * circleRadius), screenPosition.y + (AnnotationManager.COS_45 * circleRadius)));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_30 * circleRadius), screenPosition.y + (AnnotationManager.COS_30 * circleRadius)));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_15 * circleRadius), screenPosition.y + (AnnotationManager.COS_15 * circleRadius)));
                this._shapeFillVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y + circleRadius));
               
                

                break;

            case Enumerations.AnnotationLabelShape.BANNER:
                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += width / 2;


                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y));
                break;

            case Enumerations.AnnotationLabelShape.PARALL:
                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += halfWidth;

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y - halfHeight));

                break;

            case Enumerations.AnnotationLabelShape.TRAPEZ:
                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += halfWidth;

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));

                break;

            case Enumerations.AnnotationLabelShape.CLOUD:
                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += halfWidth;

                var labelCloudCount = Math.floor(halfWidth / (halfHeight * 1.25));
                labelCloudCount = Math.max(labelCloudCount, 1);
                var cloudWidth = (labelCloudCount > 0) ? (halfWidth * 2.0) / (labelCloudCount) : 0.0;
                var hCloudWidth = cloudWidth * 0.5;
                var cloudHeight = Math.min(halfHeight * 0.75, hCloudWidth);
                var labelVertexCount = 4 + 14 + (labelCloudCount * 7 * 2) + (labelCloudCount > 0 ? (labelCloudCount - 1) * 2 : 0);

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));

                for (var i = 0; i < labelCloudCount; i++) {
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + ((1 - AnnotationManager.SIN_60) * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_30 * cloudHeight)));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + ((1 - AnnotationManager.SIN_45) * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_45 * cloudHeight)));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + (AnnotationManager.SIN_30 * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_60 * cloudHeight)));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + hCloudWidth, screenPosition.y + halfHeight + cloudHeight));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + hCloudWidth + (AnnotationManager.SIN_30 * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_60 * cloudHeight)));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + hCloudWidth + (AnnotationManager.SIN_45 * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_45 * cloudHeight)));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + hCloudWidth + (AnnotationManager.SIN_60 * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_30 * cloudHeight)));


                    if (labelCloudCount > 1 && i < (labelCloudCount - 1)) {
                        this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + cloudWidth, screenPosition.y + halfHeight));
                    }
                }

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_30 * halfHeight), screenPosition.y + (AnnotationManager.COS_30 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_45 * halfHeight), screenPosition.y + (AnnotationManager.COS_45 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_60 * halfHeight), screenPosition.y + (AnnotationManager.COS_60 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_60 * halfHeight), screenPosition.y - (AnnotationManager.COS_60 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_45 * halfHeight), screenPosition.y - (AnnotationManager.COS_45 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_30 * halfHeight), screenPosition.y - (AnnotationManager.COS_30 * halfHeight)));

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));

                for (var i = 0; i < labelCloudCount; i++) {
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - ((1 - AnnotationManager.SIN_60) * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_30 * cloudHeight)));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - ((1 - AnnotationManager.SIN_45) * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_45 * cloudHeight)));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - (AnnotationManager.SIN_30 * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_60 * cloudHeight)));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - hCloudWidth, screenPosition.y - halfHeight - cloudHeight));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - hCloudWidth - (AnnotationManager.SIN_30 * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_60 * cloudHeight)));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - hCloudWidth - (AnnotationManager.SIN_45 * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_45 * cloudHeight)));
                    this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - hCloudWidth - (AnnotationManager.SIN_60 * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_30 * cloudHeight)));
                    if (labelCloudCount > 1 && i < (labelCloudCount - 1)) {
                        this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - cloudWidth, screenPosition.y - halfHeight));
                    }
                }

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_30 * halfHeight), screenPosition.y - (AnnotationManager.COS_30 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_45 * halfHeight), screenPosition.y - (AnnotationManager.COS_45 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_60 * halfHeight), screenPosition.y - (AnnotationManager.COS_60 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_60 * halfHeight), screenPosition.y + (AnnotationManager.COS_60 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_45 * halfHeight), screenPosition.y + (AnnotationManager.COS_45 * halfHeight)));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_30 * halfHeight), screenPosition.y + (AnnotationManager.COS_30 * halfHeight)));

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
                break;

            case Enumerations.AnnotationLabelShape.ARROW:
                break;

            default:
                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += width / 2;

                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
                this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));

                break;
        }
    }
    private getMarginSize(): number {

        switch (this.shapeType) {
            case Enumerations.AnnotationLabelShape.RECTANGLE:
                return AnnotationManager.RECTANGLE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.TRIANGLE:
                return AnnotationManager.TRIANGLE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.DIAMOND:
                return AnnotationManager.DIAMOND_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.PENTAGON:
                return AnnotationManager.PENTAGON_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.HEXAGON:
                return AnnotationManager.HEXAGON_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.ELLIPSE:
                return AnnotationManager.ELLIPSE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.CIRCLE:
                return AnnotationManager.CIRCLE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.BANNER:
                return AnnotationManager.BANNER_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.PARALL:
                return AnnotationManager.RECTANGLE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.TRAPEZ:
                return AnnotationManager.RECTANGLE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.CLOUD:
                return AnnotationManager.RECTANGLE_MARGIN + .5 * this._fontHeight;

            default:
                return 2;
        }
    }
}

class LabelAnnotation implements Interfaces.Interfaces.I2DAnnotation {

    public labelId: string;

    private static MARGIN = 5;

    private _drawPosition: BABYLON.Vector2;
    private _position: BABYLON.Vector2;
    private _textColor: string;
    private _backgroundColor: string;
    private _lineColor: string;
    private _lineEnabled: boolean;
    private _fontHeight: number = 0;
    private _renderCanvas: CanvasRenderingContext2D;
    private _text: string[];
    private _font: string = "";
    private _showBorder: boolean = true;
    private _textWidth: number = 0;
    private _textHeight: number = 0;
    private _shapeVerteces: BABYLON.Vector2[] = [];

    constructor(labelId: string, renderCanvas: CanvasRenderingContext2D, labelInfo: Data.GraphicData.Label) {
        this._renderCanvas = renderCanvas;
        this._position = new BABYLON.Vector2(labelInfo.Position.X, labelInfo.Position.Y);
        this._text = [];
        this.labelId = labelId;
        this._showBorder = labelInfo.LineEnabled;
        var stringPieces = labelInfo.Caption.split(/\r\n|\r|\n/);

        for (var i = 0; i < stringPieces.length; i++) {
            this._text.push(stringPieces[i]);
        }

        this._textColor = labelInfo.TextColor;
        this._backgroundColor = labelInfo.BackgroundColor;
        this._lineColor = labelInfo.LineColor;
        this._lineEnabled = labelInfo.LineEnabled;
        this._fontHeight = labelInfo.CaptionFont.Height;

        this._font = (labelInfo.CaptionFont.Bold ? "bold " : "") + (labelInfo.CaptionFont.Italic ? "italic " : "") + (labelInfo.CaptionFont.Strikeout ? "strikeout " : "") + (labelInfo.CaptionFont.Underline ? "underline " : "") + this._fontHeight + "pt " + labelInfo.CaptionFont.FaceName;

        this._renderCanvas.font = this._font;

        for (var i = 0; i < this._text.length; i++) {
            this._textWidth = Math.max(this._textWidth, this._renderCanvas.measureText(this._text[i]).width);
            this._textHeight = labelInfo.CaptionFont.Height * (i + 1) + this.getMarginSize() * i;
        }

        this.generateLabelShape();
    }


    public render(viewport: BABYLON.Viewport): void {

        this._renderCanvas.strokeStyle = this._lineColor;

        // Draw shape
        this._renderCanvas.beginPath();

        var prevStrokeStyle = this._renderCanvas.strokeStyle;

        this.generateLabelShape();

        // Draw Border and Background
        this._renderCanvas.moveTo(this._shapeVerteces[0].x, this._shapeVerteces[0].y);

        for (var i = 1; i < this._shapeVerteces.length; i++) {
            this._renderCanvas.lineTo(this._shapeVerteces[i].x, this._shapeVerteces[i].y);
        }

        this._renderCanvas.fillStyle = this._backgroundColor;
        this._renderCanvas.lineWidth = 1;
        this._renderCanvas.fill();

        if (this._lineEnabled) {
            this._renderCanvas.stroke();
        }

        // Draw Text
        this.renderText();
    }

    private renderText(): void {

        var width = this._textWidth;
        var height = this._textHeight;

        var halfWidth = (width + this.getMarginSize()) / 2.0;
        var halfHeight = (height + this.getMarginSize()) / 2.0;

        var textYPos = this._drawPosition.y + halfHeight + ((this._fontHeight)) / 2 - (this._textHeight - this._fontHeight) / 2;
        this._renderCanvas.font = this._font;
        this._renderCanvas.strokeStyle = this._textColor;
        this._renderCanvas.fillStyle = this._textColor;

        for (var i = 0; i < this._text.length; i++) {

            this._renderCanvas.fillText(this._text[i], this._drawPosition.x + this.getMarginSize()/2, textYPos);
            textYPos += this._fontHeight + this.getMarginSize();
        }
    }

    private getMarginSize(): number {
        return LabelAnnotation.MARGIN + .5 * this._fontHeight;
    }

    private generateLabelShape(): void {

        var screenPosition = this._position.clone();
        this._drawPosition = this._position.clone();

        this._shapeVerteces = [];

        this._renderCanvas.font = this._font;

        var width = this._textWidth;
        var height = this._textHeight;

        var halfWidth = (width + this.getMarginSize()) / 2.0;
        var halfHeight = (height + this.getMarginSize()) / 2.0;

        screenPosition.x += halfWidth;
        screenPosition.y += halfHeight;

        if (screenPosition.x + halfWidth > this._renderCanvas.canvas.width) {

            var shiftAmount = (screenPosition.x + halfWidth - this._renderCanvas.canvas.width)
            screenPosition.x -= shiftAmount;
            this._drawPosition.x -= shiftAmount;
        }
        if (screenPosition.y + halfHeight > this._renderCanvas.canvas.height) {

            var shiftAmount = (screenPosition.y + halfHeight - this._renderCanvas.canvas.height)
            screenPosition.y -= shiftAmount;
            this._drawPosition.y -= shiftAmount;
        }

        screenPosition.x = Math.max(0, screenPosition.x);
        screenPosition.y = Math.max(0, screenPosition.y);

        this._drawPosition.x = Math.max(0, this._drawPosition.x);
        this._drawPosition.y = Math.max(0, this._drawPosition.y);

        this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
        this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
        this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));
        this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
        this._shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
    }
}

class MarkedPointAnnotation implements Interfaces.Interfaces.I3DAnnotation {

    private static SELECTED_COLOR = new BABYLON.Color3(.0, .8, .8);

    private static testingEnum = 0;
    private _renderCanvas: CanvasRenderingContext2D;

    private _startPosition: BABYLON.Vector3;
    private _endPosition: BABYLON.Vector3;
    private _markedPointShape: Enumerations.AnnotationLabelShape;
    private _text: string[];
    private _font: string = "";
    private _lineWidth: number;
    private _textColor: string;
    private _fontHeight: number = 0;
    private _textWidth: number = 0;
    private _textHeight: number = 0;
    private _backgroundColor: string;
    private _lineColor: string;
    private _boundingBox: BABYLON.BoundingBox;
    private shapeVerteces: BABYLON.Vector2[] = [];

    public id: string;

    constructor(assetId: string, renderCanvas: CanvasRenderingContext2D, mpInfo: Data.GraphicData.MarkedPoint, material: Interfaces.MaterialInterfaces.IAnnotationMaterial) {

        this._renderCanvas = renderCanvas;
        this._startPosition = new BABYLON.Vector3(mpInfo.Position.X / 1000, mpInfo.Position.Y / 1000, mpInfo.Position.Z / 1000);
        this._endPosition = new BABYLON.Vector3(mpInfo.LabelPosition.X / 1000, mpInfo.LabelPosition.Z / 1000, mpInfo.LabelPosition.Y / 1000);
        this._text = [];
        this.id = assetId;
        var stringPieces = mpInfo.Caption ? mpInfo.Caption.split(/\r\n|\r|\n/) : [];

        for (var i = 0; i < stringPieces.length; i++) {
            this._text.push(stringPieces[i]);
        }

        this._markedPointShape = material.tagShape;

        this._lineWidth = material.lineWidth;
        this._textColor = material.captionTextColor;
        this._backgroundColor = material.backgroundColor;
        this._lineColor = material.lineColor;
        this._fontHeight = material.captionFont.Height;
        this._font = (material.captionFont.Bold ? "bold " : "") + (material.captionFont.Italic ? "italic " : "") + (material.captionFont.Strikeout ? "strikeout " : "") + (material.captionFont.Underline ? "underline " : "") + this._fontHeight + "pt " + material.captionFont.FaceName;
        this.hidden = false;

        this._renderCanvas.font = this._font

        this._boundingBox = new BABYLON.BoundingBox(this._startPosition, this._startPosition);

        for (var i = 0; i < this.text.length; i++) {
            this._textWidth = Math.max(this._textWidth, this._renderCanvas.measureText(this.text[i]).width);
            this._textHeight = material.captionFont.Height * (i + 1) + this.getMarginSize() * i;
        }
    }

    public hidden: boolean;
    public selected: boolean;

    public get startPosition(): BABYLON.Vector3 {
        return this._startPosition;
    }

    public get endPosition(): BABYLON.Vector3 {
        return this._endPosition;
    }

    public get lineColor(): string {
        if (this.selected) {
            return "#00cdcd";
        }
        else {
            return this._lineColor;
        }
    }
    public get font(): string {
        return this._font;
    }
    public get text(): string[] {
        return this._text;
    }
    public get shapeType(): Enumerations.AnnotationLabelShape {
        return this._markedPointShape;
    }

    public render(transformation: BABYLON.Matrix, viewport: BABYLON.Viewport): void {

        var viewPlanes = BABYLON.Frustum.GetPlanes(transformation);

        if (!this._boundingBox.isInFrustum(viewPlanes))
            return;

        var mpScreenPosition = this.CalculateScreenPosition(transformation, viewport);
        var mpPrimScreenPosition = this.CalculatePrimitiveScreenPosition(transformation, viewport);

        this.generateLabelShape(mpScreenPosition);

        this._renderCanvas.strokeStyle = this.lineColor;

        this.renderLeaderLine(mpPrimScreenPosition);

        // Draw shape
        this._renderCanvas.beginPath();

        var prevStrokeStyle = this._renderCanvas.strokeStyle;

        // Draw Border and Background
        this._renderCanvas.moveTo(this.shapeVerteces[0].x, this.shapeVerteces[0].y);

        for (var i = 1; i < this.shapeVerteces.length; i++) {
            this._renderCanvas.lineTo(this.shapeVerteces[i].x, this.shapeVerteces[i].y);
        }
        
        this._renderCanvas.fillStyle = this._backgroundColor;
        this._renderCanvas.lineWidth = this._lineWidth;
        this._renderCanvas.fill();
        this._renderCanvas.stroke();

        // Draw Text
        this.renderText(mpScreenPosition);
    }

    public pick(positionX : number, positionY : number): boolean {

        var picked = false;
        
        switch (this.shapeType) {
            case Enumerations.AnnotationLabelShape.CLOUD:
                {
                    var cloudVertexCount = (this.shapeVerteces.length - 4 - 14) / 2;
                    var insideVertexCount = 4;
                    var insideVertices: BABYLON.Vector2[] = [];
                    var index = 0;
                    insideVertices[index] = this.shapeVerteces[this.shapeVerteces.length - 4];
                    insideVertices[index++] = this.shapeVerteces[4];
                    insideVertices[index] = this.shapeVerteces[cloudVertexCount + 1 + 4];
                    insideVertices[index++] = this.shapeVerteces[4];
                    insideVertices[index] = this.shapeVerteces[cloudVertexCount + 1 + 4];
                    insideVertices[index++] = this.shapeVerteces[this.shapeVerteces.length - 7 - 4];
                    insideVertices[index] = this.shapeVerteces[this.shapeVerteces.length - 4];
                    insideVertices[index++] = this.shapeVerteces[this.shapeVerteces.length - 7 - 4];

                    picked = this.InsidePolygon(positionX, positionY);
                        
                    break;
                }
            default:
                {
                    picked = this.InsidePolygon(positionX, positionY);
                        
                    break;
                }
        }

        return picked;
    }

    private InsidePolygon(pointX: number, pointY: number) : boolean
    {
        var counter = 0;
        var i : number;
        var xinters : number;

        var N = this.shapeVerteces.length;

        var p1 = this.shapeVerteces[0];
        for (i = 1; i <= this.shapeVerteces.length; i++) {
            var p2 = this.shapeVerteces[i % N];
            if (pointY > Math.min(p1.y, p2.y)) {
                if (pointY <= Math.max(p1.y, p2.y)) {
                    if (pointX <= Math.max(p1.x, p2.x)) {
                        if (p1.y != p2.y) {
                            xinters = (pointY - p1.y) * (p2.x - p1.x) / (p2.y - p1.y) + p1.x;
                            if (p1.x == p2.x || pointX <= xinters)
                                counter++;
                        }
                    }
                }
            }
            p1 = p2;
        }

        if (counter % 2 == 0)
            return false;
        else
            return true;
    }

    private getMarginSize(): number {

        switch (this.shapeType) {
            case Enumerations.AnnotationLabelShape.RECTANGLE:
                return AnnotationManager.RECTANGLE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.TRIANGLE:
                return AnnotationManager.TRIANGLE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.DIAMOND:
                return AnnotationManager.DIAMOND_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.PENTAGON:
                return AnnotationManager.PENTAGON_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.HEXAGON:
                return AnnotationManager.HEXAGON_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.ELLIPSE:
                return AnnotationManager.ELLIPSE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.CIRCLE:
                return AnnotationManager.CIRCLE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.BANNER:
                return AnnotationManager.BANNER_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.PARALL:
                return AnnotationManager.RECTANGLE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.TRAPEZ:
                return AnnotationManager.RECTANGLE_MARGIN + .5 * this._fontHeight;

            case Enumerations.AnnotationLabelShape.CLOUD:
                return AnnotationManager.RECTANGLE_MARGIN + .5 * this._fontHeight;

            default:
                return 2;
        }
    }

    private generateLabelShape(screenPos: BABYLON.Vector3): void {

        var screenPosition = screenPos.clone();
        this.shapeVerteces = [];

        this._renderCanvas.font = this.font;

        var width = this._textWidth;
        var height = this._textHeight;

        switch (this.shapeType) {
            case Enumerations.AnnotationLabelShape.RECTANGLE:

                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += width / 2;

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));

                break;
            case Enumerations.AnnotationLabelShape.TRIANGLE:

                var triangleSize = AnnotationManager.TAN_60 * ((width + this.getMarginSize()) / 2.0) + ((height + this.getMarginSize()) / 2.0);

                screenPosition.x += width / 2;

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - triangleSize));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + AnnotationManager.SIN_60 * triangleSize, screenPosition.y + AnnotationManager.COS_60 * triangleSize));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - AnnotationManager.SIN_60 * triangleSize, screenPosition.y + AnnotationManager.COS_60 * triangleSize));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - triangleSize));

                break;
            case Enumerations.AnnotationLabelShape.DIAMOND:

                var diamondSize = ((height + this.getMarginSize()) / 2.0) + ((width + this.getMarginSize()) / 2.0);

                screenPosition.x += width / 2;

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y + diamondSize));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + diamondSize, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - diamondSize));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - diamondSize, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y + diamondSize));

                break;
            case Enumerations.AnnotationLabelShape.PENTAGON:
                var pentagonSize = (Math.max(width + this.getMarginSize(), height + this.getMarginSize()) / 2.0) / AnnotationManager.TAN_36;

                screenPosition.x += width / 2;

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - pentagonSize));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (pentagonSize * AnnotationManager.SIN_72), screenPosition.y - (pentagonSize * AnnotationManager.COS_72)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (pentagonSize * AnnotationManager.SIN_36), screenPosition.y + (pentagonSize * AnnotationManager.COS_36)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (pentagonSize * AnnotationManager.SIN_36), screenPosition.y + (pentagonSize * AnnotationManager.COS_36)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (pentagonSize * AnnotationManager.SIN_72), screenPosition.y - (pentagonSize * AnnotationManager.COS_72)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - pentagonSize));

                break;
            case Enumerations.AnnotationLabelShape.HEXAGON:
                var halfWidth = width / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += width / 2;

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));

                break;
            case Enumerations.AnnotationLabelShape.ELLIPSE:

                var halfWidth = width / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += width / 2;

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_30 * halfHeight), screenPosition.y - (AnnotationManager.COS_30 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_45 * halfHeight), screenPosition.y - (AnnotationManager.COS_45 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_60 * halfHeight), screenPosition.y - (AnnotationManager.COS_60 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_60 * halfHeight), screenPosition.y + (AnnotationManager.COS_60 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_45 * halfHeight), screenPosition.y + (AnnotationManager.COS_45 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_30 * halfHeight), screenPosition.y + (AnnotationManager.COS_30 * halfHeight)));

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_30 * halfHeight), screenPosition.y + (AnnotationManager.COS_30 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_45 * halfHeight), screenPosition.y + (AnnotationManager.COS_45 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_60 * halfHeight), screenPosition.y + (AnnotationManager.COS_60 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_60 * halfHeight), screenPosition.y - (AnnotationManager.COS_60 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_45 * halfHeight), screenPosition.y - (AnnotationManager.COS_45 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_30 * halfHeight), screenPosition.y - (AnnotationManager.COS_30 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));

                break;
            case Enumerations.AnnotationLabelShape.CIRCLE:
                var circleRadius = Math.sqrt(Math.pow((height / 2.0), 2) + Math.pow((width / 2.0), 2)) + this.getMarginSize();

                screenPosition.x += width / 2;

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y + circleRadius));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_15 * circleRadius), screenPosition.y + (AnnotationManager.COS_15 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_30 * circleRadius), screenPosition.y + (AnnotationManager.COS_30 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_45 * circleRadius), screenPosition.y + (AnnotationManager.COS_45 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_60 * circleRadius), screenPosition.y + (AnnotationManager.COS_60 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_75 * circleRadius), screenPosition.y + (AnnotationManager.COS_75 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + circleRadius, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_75 * circleRadius), screenPosition.y - (AnnotationManager.COS_75 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_60 * circleRadius), screenPosition.y - (AnnotationManager.COS_60 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_45 * circleRadius), screenPosition.y - (AnnotationManager.COS_45 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_30 * circleRadius), screenPosition.y - (AnnotationManager.COS_30 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + (AnnotationManager.SIN_15 * circleRadius), screenPosition.y - (AnnotationManager.COS_15 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y - circleRadius));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_15 * circleRadius), screenPosition.y - (AnnotationManager.COS_15 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_30 * circleRadius), screenPosition.y - (AnnotationManager.COS_30 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_45 * circleRadius), screenPosition.y - (AnnotationManager.COS_45 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_60 * circleRadius), screenPosition.y - (AnnotationManager.COS_60 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_75 * circleRadius), screenPosition.y - (AnnotationManager.COS_75 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - circleRadius, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_75 * circleRadius), screenPosition.y + (AnnotationManager.COS_75 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_60 * circleRadius), screenPosition.y + (AnnotationManager.COS_60 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_45 * circleRadius), screenPosition.y + (AnnotationManager.COS_45 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_30 * circleRadius), screenPosition.y + (AnnotationManager.COS_30 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - (AnnotationManager.SIN_15 * circleRadius), screenPosition.y + (AnnotationManager.COS_15 * circleRadius)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x, screenPosition.y + circleRadius));

                break;

            case Enumerations.AnnotationLabelShape.BANNER:
                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += width / 2;


                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y));
                break;

            case Enumerations.AnnotationLabelShape.PARALL:
                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += halfWidth;

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y - halfHeight));

                break;

            case Enumerations.AnnotationLabelShape.TRAPEZ:
                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += halfWidth;

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));

                break;

            case Enumerations.AnnotationLabelShape.CLOUD:
                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += halfWidth;

                var labelCloudCount = Math.floor(halfWidth / (halfHeight * 1.25));
                labelCloudCount = Math.max(labelCloudCount, 1);
                var cloudWidth = (labelCloudCount > 0) ? (halfWidth * 2.0) / (labelCloudCount) : 0.0;
                var hCloudWidth = cloudWidth * 0.5;
                var cloudHeight = Math.min(halfHeight * 0.75, hCloudWidth);
                var labelVertexCount = 4 + 14 + (labelCloudCount * 7 * 2) + (labelCloudCount > 0 ? (labelCloudCount - 1) * 2 : 0);

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));

                for (var i = 0; i < labelCloudCount; i++) {
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + ((1 - AnnotationManager.SIN_60) * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_30 * cloudHeight)));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + ((1 - AnnotationManager.SIN_45) * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_45 * cloudHeight)));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + (AnnotationManager.SIN_30 * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_60 * cloudHeight)));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + hCloudWidth, screenPosition.y + halfHeight + cloudHeight));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + hCloudWidth + (AnnotationManager.SIN_30 * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_60 * cloudHeight)));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + hCloudWidth + (AnnotationManager.SIN_45 * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_45 * cloudHeight)));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + hCloudWidth + (AnnotationManager.SIN_60 * hCloudWidth), screenPosition.y + halfHeight + (AnnotationManager.SIN_30 * cloudHeight)));


                    if (labelCloudCount > 1 && i < (labelCloudCount - 1)) {
                        this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth + (i * cloudWidth) + cloudWidth, screenPosition.y + halfHeight));
                    }
                }

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_30 * halfHeight), screenPosition.y + (AnnotationManager.COS_30 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_45 * halfHeight), screenPosition.y + (AnnotationManager.COS_45 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_60 * halfHeight), screenPosition.y + (AnnotationManager.COS_60 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + halfHeight, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_60 * halfHeight), screenPosition.y - (AnnotationManager.COS_60 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_45 * halfHeight), screenPosition.y - (AnnotationManager.COS_45 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth + (AnnotationManager.SIN_30 * halfHeight), screenPosition.y - (AnnotationManager.COS_30 * halfHeight)));

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));

                for (var i = 0; i < labelCloudCount; i++) {
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - ((1 - AnnotationManager.SIN_60) * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_30 * cloudHeight)));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - ((1 - AnnotationManager.SIN_45) * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_45 * cloudHeight)));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - (AnnotationManager.SIN_30 * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_60 * cloudHeight)));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - hCloudWidth, screenPosition.y - halfHeight - cloudHeight));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - hCloudWidth - (AnnotationManager.SIN_30 * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_60 * cloudHeight)));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - hCloudWidth - (AnnotationManager.SIN_45 * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_45 * cloudHeight)));
                    this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - hCloudWidth - (AnnotationManager.SIN_60 * hCloudWidth), screenPosition.y - halfHeight - (AnnotationManager.SIN_30 * cloudHeight)));
                    if (labelCloudCount > 1 && i < (labelCloudCount - 1)) {
                        this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth - (i * cloudWidth) - cloudWidth, screenPosition.y - halfHeight));
                    }
                }

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_30 * halfHeight), screenPosition.y - (AnnotationManager.COS_30 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_45 * halfHeight), screenPosition.y - (AnnotationManager.COS_45 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_60 * halfHeight), screenPosition.y - (AnnotationManager.COS_60 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - halfHeight, screenPosition.y));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_60 * halfHeight), screenPosition.y + (AnnotationManager.COS_60 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_45 * halfHeight), screenPosition.y + (AnnotationManager.COS_45 * halfHeight)));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth - (AnnotationManager.SIN_30 * halfHeight), screenPosition.y + (AnnotationManager.COS_30 * halfHeight)));

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
                break;

            case Enumerations.AnnotationLabelShape.ARROW:
                break;

            default:
                var halfWidth = (width + this.getMarginSize()) / 2.0;
                var halfHeight = (height + this.getMarginSize()) / 2.0;

                screenPosition.x += width / 2;

                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y + halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x + halfWidth, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y - halfHeight));
                this.shapeVerteces.push(new BABYLON.Vector2(screenPosition.x - halfWidth, screenPosition.y + halfHeight));

                break;
        }

    }


    private renderText(mpScreenPosition: BABYLON.Vector3): void {

        var textYPos = mpScreenPosition.y + ((this._fontHeight)) / 2 - (this._textHeight - this._fontHeight) / 2;
        this._renderCanvas.font = this._font;
        this._renderCanvas.strokeStyle = this._textColor;
        this._renderCanvas.fillStyle = this._textColor;

        for (var i = 0; i < this.text.length; i++) {

            this._renderCanvas.fillText(this.text[i], mpScreenPosition.x, textYPos);
            textYPos += this._fontHeight + this.getMarginSize();
        }
    }
    private renderLeaderLine(primitiveScreenPosition: BABYLON.Vector2): void {

        var currentDistance = 0.0;
        var smallestDistance = 0.0;
        var closestPointIndex = 0;

        for (var i = 0; i < this.shapeVerteces.length; i++) {
            var currentDistance = BABYLON.Vector2.Distance(this.shapeVerteces[i], primitiveScreenPosition);

            if (i == 0 || currentDistance < smallestDistance) {
                smallestDistance = currentDistance;
                closestPointIndex = i;
            }
        }

        this._renderCanvas.beginPath();
        this._renderCanvas.moveTo(primitiveScreenPosition.x, primitiveScreenPosition.y);
        this._renderCanvas.lineTo(this.shapeVerteces[closestPointIndex].x, this.shapeVerteces[closestPointIndex].y);
        this._renderCanvas.stroke();
    }
    private CalculatePrimitiveScreenPosition(transformation: BABYLON.Matrix, viewport: BABYLON.Viewport): BABYLON.Vector2 {

        var screePosition = BABYLON.Vector3.Project(this.startPosition,
            BABYLON.Matrix.Identity(),
            transformation,
            viewport);

        return new BABYLON.Vector2(screePosition.x, screePosition.y);
    }

    private CalculateScreenPosition(transformation: BABYLON.Matrix, viewport: BABYLON.Viewport): BABYLON.Vector3 {
        var screenPos = BABYLON.Vector3.Project(this.endPosition,
            BABYLON.Matrix.Identity(),
            transformation,
            viewport);

        return screenPos;
    }
}

export default AnnotationManager;